import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Chip, Alert, List, ListItem, ListItemText,
  CircularProgress, Divider,
} from '@mui/material';
import { waveService } from '../services/waveService';
import { WMS_TYPE_CHIP_SX } from '../shared/wmsTypeChip';

interface SelectedOrder {
  id: string;
  orderNumber: string;
  customerName: string;
  orderType: string;
  itemCount: number;
  desi?: number | null;
  totalDesi?: number | null;
  totalDesiValue?: number | null;
}

interface WaveLimits {
  maxOrders: number | '';
  maxItems: number | '';
  maxTotalDesi: number | '';
}

interface CreateWaveModalProps {
  open: boolean;
  warehouseId: string;
  orders: SelectedOrder[];
  waveLimits?: WaveLimits;
  onClose: () => void;
  onCreated: (waveId?: string) => void;
}

const resolveOrderType = (orderType: string, itemCount: number): 'Single' | 'Multi' => {
  // Is kurali: toplam adet 1'den buyukse siparis daima Multi'dir.
  if (Number(itemCount) > 1) return 'Multi';
  if (Number(itemCount) === 1) return 'Single';

  // Eski/veri uyumsuz kayitlar icin fallback.
  const normalizedType = (orderType || '').toLowerCase().trim();
  if (normalizedType === 'multi') return 'Multi';
  return 'Single';
};

const CreateWaveModal: React.FC<CreateWaveModalProps> = ({
  open, warehouseId, orders, waveLimits, onClose, onCreated,
}) => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const singleOrders = useMemo(
    () => orders.filter(o => resolveOrderType(o.orderType, o.itemCount) === 'Single'),
    [orders]
  );
  const multiOrders = useMemo(
    () => orders.filter(o => resolveOrderType(o.orderType, o.itemCount) === 'Multi'),
    [orders]
  );
  const isMixed = singleOrders.length > 0 && multiOrders.length > 0;
  const totalItems = orders.reduce((s, o) => s + o.itemCount, 0);

  const normalizedLimits = useMemo(() => ({
    maxOrders: typeof waveLimits?.maxOrders === 'number' && waveLimits.maxOrders > 0 ? waveLimits.maxOrders : Number.POSITIVE_INFINITY,
    maxItems: typeof waveLimits?.maxItems === 'number' && waveLimits.maxItems > 0 ? waveLimits.maxItems : Number.POSITIVE_INFINITY,
    maxTotalDesi: typeof waveLimits?.maxTotalDesi === 'number' && waveLimits.maxTotalDesi > 0 ? waveLimits.maxTotalDesi : Number.POSITIVE_INFINITY,
  }), [waveLimits]);

  const resolveOrderDesi = (order: SelectedOrder): number => {
    const raw =
      (order.totalDesiValue ?? order.totalDesi ?? order.desi);
    const parsed = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const splitOrdersByLimits = (input: SelectedOrder[]) => {
    const chunks: SelectedOrder[][] = [];
    let current: SelectedOrder[] = [];
    let currentItems = 0;
    let currentDesi = 0;

    for (const order of input) {
      const nextCount = current.length + 1;
      const nextItems = currentItems + Number(order.itemCount || 0);
      const nextDesi = currentDesi + resolveOrderDesi(order);
      const exceedsOrders = nextCount > normalizedLimits.maxOrders;
      const exceedsItems = nextItems > normalizedLimits.maxItems;
      const exceedsDesi = nextDesi > normalizedLimits.maxTotalDesi;

      if ((exceedsOrders || exceedsItems || exceedsDesi) && current.length > 0) {
        chunks.push(current);
        current = [order];
        currentItems = Number(order.itemCount || 0);
        currentDesi = resolveOrderDesi(order);
      } else {
        current.push(order);
        currentItems = nextItems;
        currentDesi = nextDesi;
      }
    }

    if (current.length > 0) chunks.push(current);
    return chunks;
  };

  const createWave = async (selectedOrders: SelectedOrder[]) => {
    setCreating(true);
    setError('');
    try {
      const chunks = splitOrdersByLimits(selectedOrders);
      let lastWaveId: string | undefined;

      for (let i = 0; i < chunks.length; i += 1) {
        const chunk = chunks[i];
        const res = await waveService.create({
          WarehouseId: warehouseId,
          OrderIds: chunk.map(o => o.id),
          // Rota stratejisi bu ekranda secilmiyor.
          // Depo tanimlama ekraninda belirlenen varsayilan strateji backend tarafinda uygulanacak.
        });
        lastWaveId = res.data?.data?.id || res.data?.id;
      }

      onCreated(lastWaveId);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'İş emri oluşturulurken hata oluştu');
    }
    setCreating(false);
  };

  const handleCreateAll = () => createWave(orders);
  const handleCreateSingle = () => createWave(singleOrders);
  const handleCreateMulti = () => createWave(multiOrders);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>İş Emri Oluştur</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2">
              Seçilen Siparişler: <strong>{orders.length}</strong> sipariş, <strong>{totalItems}</strong> ürün
            </Typography>
          </Box>

          <Alert severity="info" sx={{ py: 0.5 }}>
            Toplama algoritması depo ayarına göre kullanılır.
          </Alert>

          {(Number.isFinite(normalizedLimits.maxOrders) || Number.isFinite(normalizedLimits.maxItems) || Number.isFinite(normalizedLimits.maxTotalDesi)) && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              İş emri limitleri aktif:
              {Number.isFinite(normalizedLimits.maxOrders) ? ` Max Sipariş ${normalizedLimits.maxOrders}` : ''} 
              {Number.isFinite(normalizedLimits.maxItems) ? ` / Max Ürün ${normalizedLimits.maxItems}` : ''}
              {Number.isFinite(normalizedLimits.maxTotalDesi) ? ` / Max Desi ${normalizedLimits.maxTotalDesi}` : ''}
              . Limit aşılırsa sistem siparişleri birden fazla iş emrine böler.
            </Alert>
          )}

          {isMixed && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Tekli ve çoklu siparişler karışık. Ayrı iş emirleri oluşturmanız önerilir.
            </Alert>
          )}

          <Divider />
          <Typography variant="subtitle2">Sipariş Listesi</Typography>
          <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
            {orders.map(o => (
              <ListItem key={o.id} disablePadding sx={{ py: 0.25 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={500}>{o.orderNumber}</Typography>
                      <Typography variant="body2" color="text.secondary">{o.customerName}</Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
                      <Chip label={`${o.itemCount} ürün`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      <Chip
                        label={resolveOrderType(o.orderType, o.itemCount) === 'Single' ? 'Tekli' : 'Çoklu'}
                        size="small"
                        variant="outlined"
                        color={resolveOrderType(o.orderType, o.itemCount) === 'Single' ? 'info' : 'secondary'}
                        sx={{ ...WMS_TYPE_CHIP_SX, height: 18, fontSize: '0.65rem' }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
        {isMixed ? (
          <>
            <Button variant="outlined" onClick={handleCreateSingle} disabled={creating || singleOrders.length === 0}>
              {creating ? <CircularProgress size={18} /> : `Tekli İş Emri (${singleOrders.length})`}
            </Button>
            <Button variant="contained" onClick={handleCreateMulti} disabled={creating || multiOrders.length === 0}>
              {creating ? <CircularProgress size={18} /> : `Çoklu İş Emri (${multiOrders.length})`}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleCreateAll} disabled={creating || orders.length === 0}>
            {creating ? <CircularProgress size={18} /> : 'İş Emri Oluştur'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateWaveModal;
